import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';

import { useUser } from '@mopla/business-logic';

import passApi from '../../../api/passApi';
import { usePassengerAppNavigate } from '../../../hooks';

import { billingAddressSchema } from './manageBillingAddress.schema';

export interface IBillingAddressFormValues {
	firstName: string;
	lastName: string;
	company: string;
	street: string;
	streetNumber: string;
	postCode: string;
	location: string;
}

const defaultValues: IBillingAddressFormValues = {
	firstName: '',
	lastName: '',
	company: '',
	street: '',
	streetNumber: '',
	postCode: '',
	location: '',
};

export const useManageBillingAddressForm = () => {
	const { t } = useTranslation('manageBillingAddress');
	const { navigateToDownloadInvoices } = usePassengerAppNavigate();
	const { userData: user } = useUser();
	const [requestError, setRequestError] = useState<string | null>(null);

	const methods = useForm<IBillingAddressFormValues>({
		defaultValues,
		resolver: yupResolver(billingAddressSchema),
		mode: 'onBlur',
	});

	const {
		control,
		formState: { isDirty, isSubmitting, errors },
		handleSubmit,
		reset,
	} = methods;

	useEffect(() => {
		if (!user?.alternateInvoiceAddress) return;
		reset({
			firstName: user.alternateInvoiceAddress.firstName || '',
			lastName: user.alternateInvoiceAddress.lastName || '',
			company: user.alternateInvoiceAddress.company || '',
			street: user.alternateInvoiceAddress.street || '',
			streetNumber: user.alternateInvoiceAddress.streetNumber || '',
			postCode: user.alternateInvoiceAddress.zipCode || '',
			location: user.alternateInvoiceAddress.city || '',
		});
	}, [reset, user?.alternateInvoiceAddress]);

	const isDisabled = !isDirty || isSubmitting || Object.keys(errors).length > 0;

	const saveChanges = async (data: IBillingAddressFormValues) => {
		try {
			setRequestError('');
			const invoiceDetails = {
				firstName: data.firstName.trim(),
				lastName: data.lastName.trim(),
				company: data.company.trim(),
				street: data.street.trim(),
				streetNumber: data.streetNumber.trim(),
				zipcode: data.postCode.trim(),
				city: data.location.trim(),
			};

			await passApi.post(
				'/api/command/setAlternateInvoiceAddress',
				invoiceDetails
			);

			navigateToDownloadInvoices();
		} catch (e: unknown) {
			console.error('Error saving billing address', e);
			const axiosError = e as { response?: { data?: { error?: string } } };
			setRequestError(axiosError.response?.data?.error || t('save_error'));
		}
	};

	return {
		control,
		errors,
		isDisabled,
		requestError,
		handleSubmit,
		saveChanges,
	};
};
